<script setup lang="ts">
import type { IAuthor } from "~/types";
import { scrollToItem } from "~/helpers";
import {
  Spinner,
  Pagination,
  Alert,
  type IPaginationProps
} from "@frontiers-vue3/public-pages";
import { SearchDataModel } from "~/types/models/SearchData.model";

const itemsPerPage = useEnvironmentConfigStore().getConfig().authorsPerPage;
const terminology = useTerminologyStore();
const main = useMain();

const isDataFetchPending = ref<boolean>(false);
const authorsTerm = terminology.getTerm("authors");
const researchTopicTerm = terminology.getTerm("researchTopic");
const currentPage = ref<number>(1);
const authorsFetchError = useState<string | undefined>(
  "authorsFetchError",
  () => undefined
);
const metaData = usePageMetaInfo().getResearchTopicDetailsPageMetaInfo(
  main.currentResearchTopic,
  "/authors"
);

useHead(() => metaData);

const paginationProps = computed(
  (): IPaginationProps => ({
    itemsPerPage: itemsPerPage,
    totalItems: itemsCount.value,
    label: authorsTerm.toLocaleLowerCase(),
    pageNumber: currentPage.value,
    showFirstLastButtons: false
  })
);

const displayAlertMessage = computed((): boolean => {
  return (
    !isDataFetchPending.value &&
    !computedCurrentAuthors.value?.length &&
    !authorsFetchError.value
  );
});

const paginate = async (pageNumber: number, scroll = false): Promise<void> => {
  isDataFetchPending.value = true;
  const skip = (pageNumber - 1) * itemsPerPage;
  const availablePage = currentAuthors.value.has(pageNumber);

  if (!availablePage) {
    const { getAuthorsByResearchTopicId } = useRtApiService();
    const authors = await getAuthorsByResearchTopicId(
      main.currentResearchTopic.id,
      new SearchDataModel(skip, itemsPerPage)
    );
    currentAuthors.value.set(pageNumber, authors.items);
  }

  currentPage.value = pageNumber;
  if (scroll) scrollToItem("MainTabs");
  isDataFetchPending.value = false;
};

const { data } = useAsyncData("authors", async () => {
  isDataFetchPending.value = true;
  const { getAuthors } = useProxyService();
  const authorsData = await getAuthors(
    main.currentResearchTopic.id,
    new SearchDataModel(0, itemsPerPage),
    { getFromCache: true }
  );

  if (authorsData?.error) {
    console.error("Error fetching authors data. Error:", authorsData.error);
    authorsFetchError.value = authorsData.error;
  }

  isDataFetchPending.value = false;
  return authorsData;
});

const itemsCount = computed((): number => {
  return data.value?.authors.total || main.totalAuthors;
});

const currentAuthors = useState<Map<number, IAuthor[]>>("authors", () => {
  return new Map().set(currentPage.value, data.value?.authors.items);
});

const computedCurrentAuthors = computed(() => {
  return (
    currentAuthors.value.get(currentPage.value) || data.value?.authors.items
  );
});
</script>
<template>
  <section>
    <h2 class="Section__title">
      <span v-if="itemsCount">{{ itemsCount }}</span> {{ authorsTerm }}
    </h2>
    <div
      v-if="!!authorsFetchError && !isDataFetchPending"
      class="Authors Authors--error">
      <Alert type="error" message="Unable to retrieve component data." />
    </div>
    <div v-if="computedCurrentAuthors?.length" class="Authors">
      <ul class="Authors__CardList">
        <li v-for="author in computedCurrentAuthors" :key="author.userId">
          <AuthorCard v-bind="author" />
        </li>
      </ul>
      <Spinner
        v-if="isDataFetchPending && itemsCount"
        class="Authors__Spinner"
        size="2xl"
        display="inline-flex" />
      <pagination
        v-if="itemsCount"
        v-bind="paginationProps"
        @new-page="(pageNumber: number) => paginate(pageNumber, true)" />
    </div>

    <Spinner
      v-if="isDataFetchPending && !computedCurrentAuthors?.length"
      class="Spinner"
      size="2xl"
      display="flex" />

    <AlertMessage
      v-if="displayAlertMessage"
      :message="`The ${researchTopicTerm} has been launched recently. ${authorsTerm} will be published on this page as soon as they become available.`" />
  </section>
</template>

<style lang="scss">
.Authors {
  display: flex;
  flex-direction: column;
  @include tab-content-width;

  &__Spinner {
    align-self: center;
    margin: 0 24px 0 24px;
  }

  &__CardList {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-column-gap: 32px;
    grid-row-gap: 32px;
    margin-bottom: 56px;

    @media #{$break-laptop} {
      grid-template-columns: repeat(2, 1fr);
    }

    @media #{$break-laptop-sm} {
      max-width: var(--layout-col-14);
    }

    @media #{$break-md} {
      max-width: none;
      -moz-column-gap: 16px;
      grid-column-gap: 16px;
      column-gap: 16px;
      grid-row-gap: 24px;
      row-gap: 24px;
    }

    @media #{$break-xs} {
      grid-template-columns: repeat(1, 1fr);
    }
  }
}
</style>
